import {
  type AssetGroup,
  type AssetPayload,
  getListAssetGroupsAssetGroupsGetQueryKey,
  getListAssetsAssetsGetQueryKey,
  listAssetsAssetsGet,
  removeAssetGroupAssetGroupsAssetGroupIdDelete,
  useGetAssetAssetAssetIdGet,
  useListAssetGroupsAssetGroupsGet,
} from "@phc/common/gen/preferencesGen"
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"
import { useSnackbar } from "notistack"
import { useMemo } from "react"
import { getNonGlobalAssets } from "../../../utils/helpers/assetHelper"

const ASSET_FETCH_SIZE = 10000

interface ListAssetsResponse {
  assets?: AssetPayload[]
}

/**
 * Provide `includeGlobal = true` if you want to include the global asset
 * which contains all of a user's global settings.
 */
export const useListAssets = ({
  includeGlobal,
  excludeGroups = false,
  excludeNotifications = false,
  onlyIncludeNotifications = false,
}: {
  includeGlobal: boolean
  excludeGroups?: boolean
  excludeNotifications?: boolean
  onlyIncludeNotifications?: boolean
}) => {
  const queryKey = [
    ...getListAssetsAssetsGetQueryKey({
      exclude_groups: excludeGroups,
      exclude_notifications: excludeNotifications,
    }),
    onlyIncludeNotifications,
  ]

  const query = useQuery<ListAssetsResponse>({
    queryKey,
    queryFn: async (): Promise<ListAssetsResponse> => {
      // Backend spec returns { additionalProperties: true } so the generated
      // return type is { [key: string]: unknown }. The API actually returns
      // { assets: AssetPayload[] }. Fix in the backend spec.
      const response = await listAssetsAssetsGet({
        from: 0,
        size: ASSET_FETCH_SIZE,
        exclude_groups: excludeGroups,
        exclude_notifications: excludeNotifications,
      })
      return response as ListAssetsResponse
    },
    placeholderData: keepPreviousData,
    gcTime: 1000 * 60 * 60, // 1 hour
  })

  const { data: responseData, isFetching, isRefetching, isLoading } = query

  const data = useMemo(
    () => ({
      assets: (() => {
        const assets = responseData?.assets ?? []
        return includeGlobal ? assets : getNonGlobalAssets(assets)
      })(),
    }),
    [includeGlobal, responseData?.assets]
  )

  return {
    data,
    isLoading,
    isFetching,
    isRefetching,
  }
}

interface ListAssetGroupsResult {
  assetGroups: AssetGroup[]
}

export const useListAssetGroups = () => {
  return useListAssetGroupsAssetGroupsGet<ListAssetGroupsResult>(undefined, {
    query: {
      select: (response): ListAssetGroupsResult => ({
        assetGroups: (response.asset_groups as AssetGroup[] | undefined) ?? [],
      }),
    },
  })
}

interface AssetResponse {
  asset?: AssetPayload
}

export const useGetAsset = (asset_id: string | undefined) => {
  return useGetAssetAssetAssetIdGet<AssetResponse>(asset_id ?? "", undefined, {
    query: {
      enabled: !!asset_id,
      select: (response): AssetResponse => ({
        asset: response.asset as AssetPayload | undefined,
      }),
    },
  })
}

export const useRemoveAssetGroup = ({
  hideToast = false,
  skipQueryInvalidation = false,
}: { hideToast?: boolean; skipQueryInvalidation?: boolean } = {}) => {
  const { invalidateAssetQueries } = useInvalidateAssetQueries()
  const { enqueueSnackbar } = useSnackbar()

  return useMutation({
    mutationFn: async (variables: { asset_group_id: string }) =>
      removeAssetGroupAssetGroupsAssetGroupIdDelete(variables.asset_group_id),
    onError: err => {
      if (!hideToast) {
        enqueueSnackbar("Unable to remove this group.", { variant: "error" })
      }
      console.error(err)
    },
    retry: 5,
    onSuccess: () => {
      if (!hideToast) {
        enqueueSnackbar("Your group has been removed.", { variant: "success" })
      }
    },
    onSettled: async () => {
      if (skipQueryInvalidation) return
      await invalidateAssetQueries()
    },
  })
}

export const useInvalidateAssetQueries = () => {
  const queryClient = useQueryClient()

  const invalidateAssetQueries = async () => {
    await queryClient.invalidateQueries({
      queryKey: getListAssetsAssetsGetQueryKey(),
    })
    await queryClient.invalidateQueries({
      predicate: query =>
        typeof query.queryKey[0] === "string" &&
        query.queryKey[0].startsWith("/asset/"),
    })
    await queryClient.invalidateQueries({
      queryKey: getListAssetGroupsAssetGroupsGetQueryKey(),
    })
  }

  return { invalidateAssetQueries }
}
